// BillingPage.js
import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Flex,
  VStack,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Card, // Import Card
  CardBody, // Import CardBody
  useColorModeValue, // Import useColorModeValue
} from "@chakra-ui/react";
import { motion } from "framer-motion";

import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthProvider";
import { getUserSubscription } from "../../services/firebaseService";
import PricingPlans from "../../components/Billing/PricingPlans";
import BillingDashboard from "../../components/Billing/BillingDashboard";
import { useTranslation } from "react-i18next";


const BillingPage = () => {
  const { t, i18n } = useTranslation();

  const { user, firebaseUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [portalError, setPortalError] = useState(null);

  const cardBgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");

  // Fetch user's current subscription
  const fetchCurrentPlan = async () => {
    setLoadingSubscription(true);
    setSubscriptionError(null);
    try {
      if (!user) throw new Error(t("billingpage.user_not_logged_in"));

      console.log("Fetching subscription for user:", user.uid);
      const subscription = await getUserSubscription(user.uid);
      if (subscription) {
        console.log("Fetched subscription:", subscription);
        setCurrentPlan(subscription);
      } else {
        console.warn("No active subscription found for user.");
        setCurrentPlan(null);
      }
    } catch (err) {
      console.error("Error fetching current subscription:", err);
      setSubscriptionError(t("billingpage.fetch_subscription_error"));
    } finally {
      setLoadingSubscription(false);
    }
  };

  // Redirect to Stripe Customer Portal
  const handleManageSubscription = async () => {
    if (!currentPlan?.customerId) {
      console.warn("No Stripe customer ID available for user.");
      setPortalError(t("billingpage.missing_customer_id_error"));
      return;
    }

    setLoadingPortal(true);
    setPortalError(null);
    try {
      const token = await firebaseUser.getIdToken(); // Get Firebase Auth token
      const response = await fetch(`${import.meta.env.VITE_API_APP_BACKEND_URL}/api/create-portal-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ customerId: currentPlan.customerId }),
      });

      if (!response.ok) {
        throw new Error(t("billingpage.portal_session_error"));
      }

      const { url } = await response.json();
      window.location.href = url; // Redirect to Stripe Customer Portal
    } catch (err) {
      console.error("Error redirecting to customer portal:", err);
      setPortalError(t("billingpage.portal_redirect_error"));
    } finally {
      setLoadingPortal(false);
    }
  };

  useEffect(() => {
    fetchCurrentPlan();
  }, [user]);

  if (loadingSubscription) {
    return (
      <Box py={8} px={4} maxW="800px" mx="auto" textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>{t("billingpage.loading_subscription")}</Text>
      </Box>
    );
  }

  if (subscriptionError) {
    return (
      <Box py={8} px={4} maxW="800px" mx="auto" textAlign="center">
        <Alert status="error" mb={4}>
          <AlertIcon />
          {subscriptionError}
        </Alert>
        <Button colorScheme="teal" onClick={fetchCurrentPlan}>
        {t("billingpage.retry_button")}
        </Button>
      </Box>
    );
  }

  return (
    <Box bg="brand.200" color="brand.500" py={8} px={4} maxW="1200px" mx="auto">
      <Heading 
        as={motion.h1}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        size="2xl"
        fontWeight="bold"
        mb={4}
        color="brand.300"
        textAlign="center"
      >
      {t("billingpage.billing_page_title")}
      </Heading>

      {currentPlan ? (
        <>
          {/* <Box mb={6} p={4} bg="gray.100" borderRadius="md" shadow="md">
            <Heading size="md" mb={2}>Current Plan:</Heading>
            <Text>
              <strong>{currentPlan.name}</strong> - {currentPlan.price} ({currentPlan.billingCycle})
            </Text>
            <Text>Start Date: {new Date(currentPlan.currentPeriodStart * 1000).toLocaleDateString()}</Text>
            <Text>Next Billing Date: {new Date(currentPlan.currentPeriodEnd * 1000).toLocaleDateString()}</Text>
            <Text>Status: {currentPlan.status}</Text>
          </Box> */}

          <Button
            colorScheme="teal"
            mt={4}
            ml={8}
            onClick={handleManageSubscription}
            isLoading={loadingPortal}
          >
            {t("billingpage.manage_subscription_button")}
            </Button>

          <BillingDashboard userId={user?.uid} />
          <Heading textAlign="center" size="lg" mb={10} color="brand.600">
          {t("billingpage.upgrade_plans_title")}
          </Heading>
          <PricingPlans currentPlan={currentPlan} />

        </>
      ) : (
        <>
          <Heading textAlign="center" size="lg" mb={10} color="brand.600">
          {t("billingpage.subscribe_message")}
          </Heading>
          <PricingPlans currentPlan={null} showNoSubscriptionMessage={true} />
        </>
      )}
    </Box>
  );
};

export default BillingPage;
